import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSecureStorage } from '@/hooks/useSecureStorage';
import { useInputValidation } from '@/hooks/useInputValidation';

/**
 * InviteCapture:
 * - Reads inviteToken and email from the URL (?inviteToken=...&email=...)
 * - Saves them to localStorage for post-auth processing
 * - Cleans the URL to avoid showing tokens to the user
 */
const InviteCapture: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setSecureItem } = useSecureStorage();
  const { validateEmail, sanitizeInput } = useInputValidation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const inviteToken = params.get('inviteToken');
    const email = params.get('email');

    if (inviteToken && email) {
      // Validate and sanitize the email
      const emailValidation = validateEmail(email);
      if (!emailValidation.isValid) {
        console.error('[InviteCapture] Invalid email in invitation URL');
        navigate(location.pathname, { replace: true });
        return;
      }

      // Sanitize the invite token (should be UUID)
      const sanitizedToken = sanitizeInput(inviteToken);
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      
      if (!uuidRegex.test(sanitizedToken)) {
        console.error('[InviteCapture] Invalid invitation token format');
        navigate(location.pathname, { replace: true });
        return;
      }

      // Securely logged invitation capture (no sensitive data exposed)
      
      // Store securely encrypted (async)
      const storeSecurely = async () => {
        await setSecureItem('inviteToken', sanitizedToken);
        await setSecureItem('inviteEmail', email.toLowerCase().trim());
        
        // Clean the URL while keeping the same path
        navigate(location.pathname, { replace: true });
      };
      
      storeSecurely();
    }
  }, [location.search, location.pathname, navigate, setSecureItem, validateEmail, sanitizeInput]);

  return null;
};

export default InviteCapture;
